import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function fmtInr(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtPercent(value) {
  const n = Number(value) || 0;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function trafficEmoji(light) {
  if (light === 'green') return '🟢';
  if (light === 'yellow') return '🟡';
  return '🔴';
}

function trafficStatusKey(light) {
  if (light === 'green') return 'recoveryOnTrack';
  if (light === 'yellow') return 'recoveryNeedsAttention';
  return 'recoveryActionRequired';
}

function marketPositionEmoji(position) {
  if (position === 'below') return '🔴';
  if (position === 'competitive') return '🟡';
  if (position === 'above') return '🟢';
  return null;
}

function marketPositionLabelKey(position) {
  if (position === 'below') return 'marketBelowMarket';
  if (position === 'competitive') return 'marketCompetitive';
  if (position === 'above') return 'marketAboveMarket';
  return null;
}

function phoneDigits(raw) {
  return String(raw || '').replace(/\D/g, '');
}

function ownerTelHref(phone) {
  const digits = phoneDigits(phone);
  if (digits.length === 10) return `tel:+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `tel:+${digits}`;
  if (digits.length >= 10) return `tel:+${digits}`;
  return null;
}

function ownerWaHref(phone) {
  const digits = phoneDigits(phone);
  if (digits.length === 10) return `https://wa.me/91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `https://wa.me/${digits}`;
  if (digits.length >= 10) return `https://wa.me/${digits}`;
  return null;
}

function ownershipTypeKey(value) {
  if (value === 'new') return 'machineOwnershipNew';
  if (value === 'used_first_owner') return 'machineOwnershipUsed';
  if (value === 'second_hand') return 'machineOwnershipSecondHand';
  return null;
}

function MachineFinancialDetailDrawer({ row, t, onClose }) {
  const machineTitle = row.machine_name || row.machine_id;
  const ownershipKey = ownershipTypeKey(row.ownership_type);
  const telLink = ownerTelHref(row.owner_phone);
  const waLink = ownerWaHref(row.owner_phone);
  const fuelLph = row.fuel_consumption_liters_per_hour;

  return (
    <div className="rounded-lg border border-de-gold/25 bg-black/30 p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <h4 className="m-0 text-sm font-bold text-de-gold">{t('adminMachineDetail')}</h4>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-white/15 px-2 py-1 text-[10px] font-semibold text-de-muted hover:border-de-gold/40 hover:text-de-gold"
        >
          {t('hide')}
        </button>
      </div>
      <div className="space-y-2 text-xs text-de-text">
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('machine')}:</span>{' '}
          {machineTitle}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('adminFleetOwnerCol')}:</span>{' '}
          {row.owner_name || '—'}
          {row.owner_phone ? (
            <>
              {' '}| {t('phone')}: {row.owner_phone}
            </>
          ) : null}
        </p>
        <hr className="my-2 border-white/10" />
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('machinePurchasePrice')}:</span>{' '}
          {fmtInr(row.purchase_price)}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('machineOwnershipType')}:</span>{' '}
          {ownershipKey ? t(ownershipKey) : (row.ownership_type || '—')}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('adminTotalEarned')}:</span>{' '}
          {fmtInr(row.total_earned)}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('adminAvgRecovery')}:</span>{' '}
          {Number(row.recovery_percent || 0).toFixed(1)}%
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('plRemainingLabel')}:</span>{' '}
          {fmtInr(row.remaining_amount)}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('adminBreakEvenRate')}:</span>{' '}
          {fmtInr(row.break_even_rate)}/hr
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('adminProfitMargin')}:</span>{' '}
          {fmtPercent(row.profit_margin_percent)}
        </p>
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('marketPosition')}:</span>{' '}
          {marketPositionLabelKey(row.market_position) ? (
            <span className="inline-flex items-center gap-1">
              <span>{marketPositionEmoji(row.market_position)}</span>
              <span>{t(marketPositionLabelKey(row.market_position))}</span>
            </span>
          ) : '—'}
        </p>
        {row.market_min && row.market_max ? (
          <>
            <p className="m-0">
              {`${t('marketRange').replace('{{city}}', '—')}: ${fmtInr(row.market_min)} – ${fmtInr(row.market_max)}/hr`}
            </p>
            <p className="m-0 text-de-muted">
              {t('marketDataPoints').replace('{{count}}', String(row.market_data_points || 0))}
            </p>
          </>
        ) : null}
        {row.monthly_emi != null && Number(row.monthly_emi) > 0 ? (
          <p className="m-0">
            <span className="font-semibold text-de-muted">{t('machineLoanEmi')}:</span>{' '}
            {fmtInr(row.monthly_emi)}
          </p>
        ) : null}
        <p className="m-0">
          <span className="font-semibold text-de-muted">{t('machineFuelConsumption')}:</span>{' '}
          {fuelLph != null && Number.isFinite(Number(fuelLph))
            ? `${Number(fuelLph)} L/hr`
            : '—'}
        </p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {telLink ? (
          <a
            href={telLink}
            className="inline-flex items-center gap-1 rounded-lg border border-de-gold/40 bg-de-gold/10 px-3 py-2 text-xs font-semibold text-de-gold no-underline hover:bg-de-gold/20"
          >
            📞 {t('adminCallOwner')}
          </a>
        ) : null}
        {waLink ? (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-300 no-underline hover:bg-emerald-500/20"
          >
            💬 {t('adminWhatsappOwner')}
          </a>
        ) : null}
      </div>
    </div>
  );
}

const FILTER_OPTIONS = [
  { value: 'all', emoji: '', labelKey: 'adminKycAllFilter' },
  { value: 'green', emoji: '🟢', labelKey: 'recoveryOnTrack' },
  { value: 'yellow', emoji: '🟡', labelKey: 'recoveryNeedsAttention' },
  { value: 'red', emoji: '🔴', labelKey: 'recoveryActionRequired' },
];

export default function AdminMachineFinancialPanel({ authToken }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedMachineId, setExpandedMachineId] = useState(null);

  const loadFinancialIntelligence = useCallback(async () => {
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/machines/financial-intelligence`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setData(json);
    } catch {
      setData(null);
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  useEffect(() => {
    loadFinancialIntelligence();
  }, [loadFinancialIntelligence]);

  const allMachines = useMemo(
    () => (Array.isArray(data?.machines) ? data.machines : []),
    [data],
  );

  const filteredMachines = useMemo(() => {
    if (filter === 'all') return allMachines;
    return allMachines.filter((row) => row.traffic_light === filter);
  }, [allMachines, filter]);

  const summaryCards = [
    {
      icon: '💰',
      val: fmtInr(data?.summary?.total_invested),
      label: t('adminTotalInvested'),
    },
    {
      icon: '📈',
      val: fmtInr(data?.summary?.total_earned_platform),
      label: t('adminTotalEarned'),
    },
    {
      icon: '📊',
      val: `${Number(data?.summary?.avg_recovery_percent || 0).toFixed(1)}%`,
      label: t('adminAvgRecovery'),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="de-stat-ring h-10 w-10">
            <TrendingUp className="h-5 w-5 text-de-gold" />
          </div>
          <h2 className="m-0 text-lg font-bold text-de-gold">{t('adminFinancialIntelligence')}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map(({ value, emoji, labelKey }) => {
            const active = filter === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                  active
                    ? 'border-de-gold bg-de-gold/20 text-de-gold'
                    : 'border-de-gold/35 bg-black/20 text-de-muted hover:border-de-gold/60',
                )}
              >
                {emoji ? `${emoji} ` : ''}{t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {loading ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          </div>
        ) : error ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
            <button
              type="button"
              onClick={loadFinancialIntelligence}
              className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
            >
              {t('retryLabel')}
            </button>
          </div>
        ) : (
          summaryCards.map((card) => (
            <div key={card.label} className="de-ultra-card p-5 text-center">
              <p className="m-0 text-2xl">{card.icon}</p>
              <p className="m-0 mt-2 text-xl font-bold text-white">{card.val}</p>
              <p className="m-0 mt-1 text-[11px] uppercase tracking-wide text-de-muted">{card.label}</p>
            </div>
          ))
        )}
      </div>

      <div className="de-ultra-card overflow-hidden">
        <div className="border-b border-de-gold/15 px-5 py-4">
          <h3 className="m-0 text-sm font-bold text-white">{t('adminFinancialIntelligence')}</h3>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          ) : error ? (
            <p className="m-0 text-sm text-de-accent">{error}</p>
          ) : allMachines.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminNoFinancialData')}</p>
          ) : filteredMachines.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminNoFinancialData')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] border-collapse text-xs">
                <thead>
                  <tr className="border-b border-white/10">
                    {[
                      t('machine'),
                      t('adminFleetOwnerCol'),
                      t('machinePurchasePrice'),
                      t('adminTotalEarned'),
                      t('adminAvgRecovery'),
                      t('adminBreakEvenRate'),
                      t('adminProfitMargin'),
                      t('marketPosition'),
                      t('status'),
                      t('machineLoanEmi'),
                    ].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-de-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredMachines.map((row) => {
                    const machineLabel = row.machine_name
                      ? `${row.machine_id} · ${row.machine_name}`
                      : row.machine_id;
                    const expanded = expandedMachineId === row.machine_id;
                    return (
                      <React.Fragment key={row.machine_id}>
                        <tr
                          className={cn(
                            'cursor-pointer border-b border-white/5 transition-colors hover:bg-de-gold/5',
                            expanded && 'bg-de-gold/5',
                          )}
                          onClick={() => setExpandedMachineId(expanded ? null : row.machine_id)}
                        >
                          <td className="px-2 py-2 font-bold text-de-gold">{machineLabel}</td>
                          <td className="px-2 py-2 text-de-text">
                            <div>{row.owner_name || '—'}</div>
                            {row.owner_phone ? (
                              <div className="text-[10px] text-de-muted">{row.owner_phone}</div>
                            ) : null}
                          </td>
                          <td className="px-2 py-2 font-bold text-de-gold">{fmtInr(row.purchase_price)}</td>
                          <td className="px-2 py-2 font-bold text-de-gold">{fmtInr(row.total_earned)}</td>
                          <td className="px-2 py-2 text-de-text">{Number(row.recovery_percent || 0).toFixed(1)}%</td>
                          <td className="px-2 py-2 text-de-text">{fmtInr(row.break_even_rate)}/hr</td>
                          <td className="px-2 py-2 text-de-text">{fmtPercent(row.profit_margin_percent)}</td>
                          <td className="px-2 py-2 text-de-text">
                            {marketPositionLabelKey(row.market_position) ? (
                              <span className="inline-flex items-center gap-1">
                                <span>{marketPositionEmoji(row.market_position)}</span>
                                <span>{t(marketPositionLabelKey(row.market_position))}</span>
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <span className="inline-flex items-center gap-1 text-de-text">
                              <span>{trafficEmoji(row.traffic_light)}</span>
                              <span>{t(trafficStatusKey(row.traffic_light))}</span>
                            </span>
                          </td>
                          <td className="px-2 py-2 text-de-text">
                            {row.monthly_emi != null && Number(row.monthly_emi) > 0
                              ? fmtInr(row.monthly_emi)
                              : '—'}
                          </td>
                        </tr>
                        {expanded ? (
                          <tr className="border-b border-white/5">
                            <td colSpan={10} className="px-2 py-3">
                              <MachineFinancialDetailDrawer
                                row={row}
                                t={t}
                                onClose={() => setExpandedMachineId(null)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
